import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import {ActiveElement, Chart, ChartConfiguration, ChartDataset, CoreScaleOptions, LegendItem, Point, registerables, Scale, ScriptableContext, TimeScaleTimeOptions, Tooltip, TooltipItem, TooltipLabelStyle, TooltipModel, ChartTypeRegistry} from 'chart.js';
import annotationPlugin, {AnnotationPluginOptions} from '../../../../../../../../external-libs/chartjs-plugin-annotation/index.js';
import {
    LearningProgressHistoryResponseDto
} from '../../../../../services/rest/api-types/LearningProgressHistoryResponseDto';
import {
    LearningHistoryResponseDto,
    LearningHistoryTypes
} from '../../../../../services/rest/api-types/LearningHistoryResponseDto';
import {compareAsc, endOfWeek, getWeek, isAfter, Locale, startOfWeek, subHours} from 'date-fns';
import {de as dfDe, enUS as dfEn, fr as dfFr, it as dfIt, nl as dfNl} from 'date-fns/locale';
import {TranslateService} from '@ngx-translate/core';
import {TimePeriodMode} from '../../../../../services/rest/api-types/TimePeriodMode';
import 'chartjs-adapter-date-fns';
import {format} from 'date-fns-tz';
import {HistoryDiagramCalculator} from './HistoryDiagramCalculator';
import {CssColorsGetter} from '../../CssColorsGetter';

@Component({
    selector: 'mm-learning-history-diagram',
    templateUrl: './learning-history-diagram.component.html',
    styleUrl: './learning-history-diagram.component.scss',
    standalone: false
})
export class LearningHistoryDiagramComponent implements OnChanges, OnDestroy, AfterViewInit {
    @ViewChild('learningChartCanvas', {static: true}) canvasRef!: ElementRef<HTMLCanvasElement>;

    @Input({required: true})
    public inputData!: { data: LearningProgressHistoryResponseDto, timePeriodMode: TimePeriodMode };

    public timePeriodMode!: TimePeriodMode;
    public data!: LearningProgressHistoryResponseDto;

    private chart?: Chart;

    private static POINT_HOVER_RADIUS = 5;
    private static BORDER_WIDTH = 1.5;

    private static readonly LOCALE_BY_PREFIX: Readonly<Record<string, Locale>> = {
        de: dfDe,
        en: dfEn,
        fr: dfFr,
        it: dfIt,
        nl: dfNl
    };

    constructor(private host: ElementRef<HTMLElement>,
                private translate: TranslateService) {
        const chartWithFlag = Chart as unknown as { __mmRegistered?: boolean };
        if (chartWithFlag.__mmRegistered !== true) {
            Chart.register(...registerables, annotationPlugin);
            chartWithFlag.__mmRegistered = true;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (Tooltip.positioners as any).leftOffset = (elements: readonly ActiveElement[], eventPosition: Point): any => {
            const position = Tooltip.positioners.average.call(this as unknown as TooltipModel<keyof ChartTypeRegistry>, elements, eventPosition);
            // eslint-disable-next-line no-null/no-null
            if (position !== false && position.x !== null) {

                const chart = this.chart!;
                const tooltip = chart.tooltip!;
                const area = chart.chartArea;
                const middleY = (area.top + area.bottom) / 2;
                const firstEl = elements[0];
                if (firstEl?.element?.y < middleY) {
                    tooltip.options.yAlign = 'top';
                    tooltip.options.caretPadding = 0;
                } else {
                    tooltip.options.yAlign = 'bottom';
                    tooltip.options.caretPadding = 75;
                }
                position.x += 10;
            }
            return position;
        };
    }

    public ngAfterViewInit(): void {
        this.buildOrUpdateChart();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if ('inputData' in changes) {
            this.data = this.inputData.data;
            this.timePeriodMode = this.inputData.timePeriodMode;
            this.buildOrUpdateChart();
        }
    }

    public ngOnDestroy(): void {
        this.destroyChart();
    }

    private destroyChart(): void {
        if (this.chart !== undefined) {
            this.chart.destroy();
            this.chart = undefined;
        }
    }

    private getNormalizedLang(defaultLang: string = 'de'): string {
        const curr = this.translate.currentLang;
        return (curr ?? defaultLang).slice(0, 2).toLowerCase();
    }

    private dateFnsLocale(): Locale {
        const lang = this.getNormalizedLang();
        return LearningHistoryDiagramComponent.LOCALE_BY_PREFIX[lang] ?? dfDe;
    }

    private getLegendTitlePerMode(ms: number): string[] {
        const locale = this.dateFnsLocale();
        const text = this.formatTickByMode(ms);

        if (this.timePeriodMode === TimePeriodMode.Weekly) {
            const date = this.getDateInUTC(ms);
            const start = startOfWeek(date, {locale, weekStartsOn: 1});
            const end = endOfWeek(date, {locale, weekStartsOn: 1});
            const weekInfo = `${format(start, 'd.M')} - ${format(end, 'd.M.yyyy')}`;
            return [text, weekInfo];
        }
        return [text];
    }

    private formatTickByMode(ms: number): string {
        const date = this.getDateInUTC(ms);
        const locale = this.dateFnsLocale();
        switch (this.timePeriodMode) {
            case TimePeriodMode.Daily:
                return format(date, 'dd.MM', {timeZone: 'UTC'});
            case TimePeriodMode.Weekly: {
                const weekNo = getWeek(date, {weekStartsOn: 1});
                const p = this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.weekShortVersion');
                return `${p} ${weekNo}`;
            }
            case TimePeriodMode.Monthly:
                return format(date, 'MMM yy', {locale, timeZone: 'UTC'});
            default:
                return format(date, 'dd.MM', {locale, timeZone: 'UTC'});
        }
    }

    // TODO have to be removed when fixing UTC
    private getDateInUTC(ms: number): Date {
        const localOffsetMinutes = new Date(ms).getTimezoneOffset();
        const localOffsetHours = -localOffsetMinutes / 60;
        return subHours(ms, localOffsetHours);
    }

    private getCssColors(): { [key: string]: string } {
        const nativeElement = this.host.nativeElement;
        return {
            grid: CssColorsGetter.get(nativeElement, '--grid'),
            progress: CssColorsGetter.get(nativeElement, '--progress'),
            forget: CssColorsGetter.get(nativeElement, '--forget'),
            goal: CssColorsGetter.get(nativeElement, '--goal'),
            ansCorrect: CssColorsGetter.get(nativeElement, '--ans-correct'),
            ansWrong: CssColorsGetter.get(nativeElement, '--ans-wrong'),
            markerNow: CssColorsGetter.get(nativeElement, '--marker-now'),
            markerForget: CssColorsGetter.get(nativeElement, '--marker-forget'),
            projection: CssColorsGetter.get(nativeElement, '--feature-progress'),
            forgettingArea: CssColorsGetter.get(nativeElement, '--forgetting-area'),
            tick: CssColorsGetter.get(nativeElement, '--tick')
        };
    }

    private getInsertIndex(dates: string[], newDate: string): number {
        const index = dates.findIndex(d => compareAsc(new Date(d), new Date(newDate)) > 0);
        return index === -1 ? dates.length : index;
    }

    private buildOrUpdateChart(): void {
        const raw: LearningProgressHistoryResponseDto = this.data ?? {
            learningHistory: [],
            initialLearningState: undefined,
            currentLearningState: undefined,
            pointOfForgettingLearningState: undefined,
            hasLearningGoal: false,
            currentLearningGoal: 0,
            pointToLearn: undefined,
            pointOfForgetting: undefined
        };

        const history = raw.learningHistory ?? [];
        const fullHistory = raw.initialLearningState !== undefined ? [{
            ...raw.initialLearningState,
            amountOfCorrectAnswers: 0,
            amountOfWrongAnswers: 0
        }, ...history] : history;

        const nowDate = new Date();
        const periodEndTimestamps: number[] = Array.from(new Set((fullHistory as LearningHistoryResponseDto[]).filter(data => data.type === LearningHistoryTypes.PERIOD).map(h => new Date(h.periodEndDate).getTime()).filter((n): n is number => n !== undefined))).sort((a, b) => a - b);
        const periodEndDates: Date[] = periodEndTimestamps.map(ms => new Date(ms));

        const nowIdx = this.getInsertIndex(fullHistory.map(d => d.periodEndDate), raw.currentLearningState.periodEndDate);
        fullHistory.splice(nowIdx, 0, raw.currentLearningState);

        const nowIdx1 = this.getInsertIndex(periodEndTimestamps.map(ms => new Date(ms).toISOString()), raw.currentLearningState.periodEndDate);

        const pointOfForgettingPeriodEndDate = raw.pointOfForgettingLearningState?.periodEndDate;
        let pointOfForgettingIdx = pointOfForgettingPeriodEndDate !== undefined && isAfter(pointOfForgettingPeriodEndDate, nowDate) && !isAfter(pointOfForgettingPeriodEndDate, periodEndDates[periodEndDates.length - 1]) ? this.getInsertIndex(fullHistory.map(d => d.periodEndDate), raw.pointOfForgettingLearningState.periodEndDate) : undefined;
        if (pointOfForgettingIdx !== undefined) {
            fullHistory.splice(pointOfForgettingIdx, 0, raw.pointOfForgettingLearningState);
            const pointOfForgettingLearningState1 = {...raw.pointOfForgettingLearningState};
            pointOfForgettingLearningState1.reliableLearningProgress = pointOfForgettingLearningState1.reliableLearningProgress - pointOfForgettingLearningState1.forgetting;
            pointOfForgettingLearningState1.forgetting = 0;
            fullHistory.splice(pointOfForgettingIdx, 0, pointOfForgettingLearningState1);
            pointOfForgettingIdx++;
        }
        const pointBeforeForgettingIdx = pointOfForgettingIdx !== undefined ? pointOfForgettingIdx - 1 : undefined;

        const progress = fullHistory.map((h: LearningHistoryResponseDto) => h.reliableLearningProgress ?? 0);
        const forgetting = fullHistory.map((h: LearningHistoryResponseDto) => h.forgetting ?? 0);
        const isProjection = fullHistory.map((h: LearningHistoryResponseDto) => !!h.isProjection);
        const correctAnswersData = fullHistory.map((h: LearningHistoryResponseDto) => h.amountOfCorrectAnswers ?? 0);
        const wrongAnswersData = fullHistory.map((h: LearningHistoryResponseDto) => h.amountOfWrongAnswers ?? 0);

        const totals = fullHistory.map(h => (h.amountOfCorrectAnswers ?? 0) + (h.amountOfWrongAnswers ?? 0));
        const maxAnswers = totals.length > 0 ? Math.max(...totals) : 0;
        const steps = HistoryDiagramCalculator.getAnswersScaleSteps(maxAnswers);
        const yAnswersMax = steps * 10;

        const forgettingCurve = progress.map((p, i) => p - (forgetting[i] ?? 0));

        const hasGoal = !!raw.hasLearningGoal;
        const goalValue = typeof raw.currentLearningGoal === 'number' ? raw.currentLearningGoal : 0;
        const pointOfForgettingDate = raw.pointOfForgetting !== undefined ? new Date(raw.pointOfForgetting) : undefined;
        const periodRanges = fullHistory.map(h => [new Date(h.periodStartDate), new Date(h.periodEndDate)] as const);

        const progressData = progress.map((val, i) => ({
            x: periodRanges[i]?.[1]?.getTime(),
            y: val
        }));

        const forgettingCurveData = forgettingCurve.map((val, i) => ({
            x: periodRanges[i]?.[1]?.getTime(),
            y: val
        }));

        const correctAnswers = correctAnswersData.map((val, i) => ({
            x: periodRanges[i]?.[1]?.getTime(),
            y: val
        }));
        const wrongAnswers = wrongAnswersData.map((val, i) => ({
            x: periodRanges[i]?.[1]?.getTime(),
            y: val
        }));

        const goalLine = periodRanges.map(range => ({
            x: range?.[1]?.getTime(),
            y: goalValue
        }));

        const colors = this.getCssColors();

        const spreadElements: ChartDataset<'line'>[] = [
            {
                type: 'line',
                label: this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.learningGoalLabel'),
                data: hasGoal ? goalLine : Array(periodEndDates.length).fill(NaN),
                borderColor: colors.goal,
                pointBackgroundColor: colors.goal,
                borderWidth: LearningHistoryDiagramComponent.BORDER_WIDTH,
                pointRadius: 0,
                tension: 0,
                yAxisID: 'y',
                order: 40,
                spanGaps: true,
                parsing: false,
                hidden: !hasGoal,
                clip: false,
                pointHitRadius: 5,
                pointHoverBackgroundColor: colors.goal,
                pointHoverBorderColor: colors.goal,
                pointHoverRadius: (ctx): number => {
                    const idx = ctx.dataIndex;
                    if (fullHistory[idx].type === LearningHistoryTypes.MAX) {
                        return 0;
                    }
                    return idx === nowIdx || idx === pointBeforeForgettingIdx ? 0 : LearningHistoryDiagramComponent.POINT_HOVER_RADIUS;
                }
            },
            {
                type: 'line',
                label: this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.forgettingLabel'),
                data: forgettingCurveData,
                borderColor: colors.forget,
                pointBackgroundColor: colors.forget,
                borderWidth: LearningHistoryDiagramComponent.BORDER_WIDTH,
                pointRadius: (ctx): number => {
                    const idx = ctx.dataIndex as number;
                    return idx === pointOfForgettingIdx ? 2.8 : 0;
                },
                tension: 0,
                yAxisID: 'y',
                order: 20,
                spanGaps: true,
                parsing: false,
                pointHitRadius: (ctx): number => {
                    const idx = ctx.dataIndex;
                    return idx === nowIdx || idx === pointBeforeForgettingIdx ? 0 : LearningHistoryDiagramComponent.POINT_HOVER_RADIUS;
                },
                pointHoverRadius: (ctx): number => {
                    const idx = ctx.dataIndex;
                    if (fullHistory[idx].type === LearningHistoryTypes.MAX) {
                        return 0;
                    }
                    return idx === nowIdx || idx === pointBeforeForgettingIdx ? 0 : LearningHistoryDiagramComponent.POINT_HOVER_RADIUS;
                },
                segment: {
                    borderDash: (ctx): number[] => {
                        const idx = ctx.p1DataIndex as number;
                        const prevIdx = ctx.p0DataIndex as number;
                        return (isProjection[prevIdx] && isProjection[idx]) ? [4, 3] : [];
                    },
                    borderColor: (ctx): string | undefined => {
                        const idx = ctx.p1DataIndex as number;
                        const prevIdx = ctx.p0DataIndex as number;
                        const hasForgetting = Math.abs(forgettingCurve[idx] - progress[idx]) > 0.01 ||
                            Math.abs(forgettingCurve[prevIdx] - progress[prevIdx]) > 0.01;
                        return hasForgetting ? colors.forget : 'transparent';
                    }
                },
                clip: false
            },
            {
                type: 'line',
                label: this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.learningProgressLabel'),
                data: progressData,
                borderColor: colors.progress,
                pointBackgroundColor: (context): string => this.getLearningProgressLineColor(context, colors, isProjection),
                pointBorderColor: (context): string => this.getLearningProgressLineColor(context, colors, isProjection),
                backgroundColor: colors.forgettingArea,
                borderWidth: LearningHistoryDiagramComponent.BORDER_WIDTH,
                tension: 0,
                pointRadius: (ctx): number => {
                    const idx = ctx.dataIndex as number;
                    if (idx === nowIdx || idx === pointBeforeForgettingIdx || fullHistory[idx].type === LearningHistoryTypes.MAX) {
                        return 0;
                    }
                    return 2.5;
                },
                pointHoverRadius: (ctx): number => {
                    const idx = ctx.dataIndex;
                    if (fullHistory[idx].type === LearningHistoryTypes.MAX) {
                        return 0;
                    }
                    return idx === nowIdx || idx === pointBeforeForgettingIdx ? 0 : LearningHistoryDiagramComponent.POINT_HOVER_RADIUS;
                },
                yAxisID: 'y',
                order: 10,
                spanGaps: true,
                parsing: false,
                fill: {target: '-1', above: colors.forgettingArea, below: colors.forgettingArea},
                segment: {
                    borderColor: (ctx): string => {
                        const idx = ctx.p1DataIndex as number;
                        const prevIdx = ctx.p0DataIndex as number;

                        if (idx === pointOfForgettingIdx) {
                            return 'transparent';
                        }
                        return (isProjection[prevIdx] && isProjection[idx]) ? colors.projection : colors.progress;
                    },
                    borderDash: (ctx): number[] => {
                        const idx = ctx.p1DataIndex as number;
                        const prevIdx = ctx.p0DataIndex as number;
                        return (isProjection[prevIdx] && isProjection[idx]) ? [6, 5] : [];
                    }
                },
                clip: false,
                pointHitRadius: (ctx): number => {
                    const idx = ctx.dataIndex;
                    return idx === nowIdx || idx === pointBeforeForgettingIdx ? 0 : LearningHistoryDiagramComponent.POINT_HOVER_RADIUS;
                }
            }
        ];

        const config: ChartConfiguration<'bar'> = {
            type: 'bar',
            data: {
                labels: periodEndTimestamps,
                datasets: [
                    {
                        type: 'bar',
                        label: this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.correctAnswersLabel'),
                        stack: 'answers',
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        data: correctAnswers as any,
                        backgroundColor: colors.ansCorrect,
                        borderSkipped: false,
                        order: 50,
                        xAxisID: 'xBars',
                        yAxisID: 'yAnswers',
                        barPercentage: 0.8,
                        barThickness: 36,
                        borderColor: 'transparent',
                        borderWidth: {top: 0, right: 4, bottom: 0, left: 0},
                        hoverBackgroundColor: colors.ansCorrect
                    },
                    {
                        type: 'bar',
                        label: this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.incorrectAnswersLabel'),
                        stack: 'answers',
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        data: wrongAnswers as any,
                        backgroundColor: colors.ansWrong,
                        borderSkipped: false,
                        order: 50,
                        xAxisID: 'xBars',
                        yAxisID: 'yAnswers',
                        barPercentage: 0.8,
                        barThickness: 36,
                        borderColor: 'transparent',
                        borderWidth: {top: 0, right: 4, bottom: 0, left: 0},
                        hoverBackgroundColor: colors.ansWrong
                    },
                    ...spreadElements as unknown as ChartDataset<'bar'>[]
                ]
            },
            options: {
                maintainAspectRatio: false,
                responsive: true,
                layout: {padding: 0},
                interaction: {mode: 'index', intersect: true},
                plugins: {
                    legend: {
                        position: 'bottom',
                        display: true,
                        onClick: (e, legendItem, legend) => {
                            const index = legendItem.datasetIndex;
                            // eslint-disable-next-line no-null/no-null
                            if (index === null || index === undefined) {
                                const nowLabel = this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.nowLabel');

                                if (legendItem.text === nowLabel) {
                                    const annotationOptions = legend.chart.options.plugins?.annotation;
                                    if (annotationOptions?.annotations?.now !== undefined) {
                                        const currentDisplay = annotationOptions.annotations.now.display;
                                        annotationOptions.annotations.now.display = currentDisplay === false;
                                        legendItem.hidden = annotationOptions.annotations.now.display === false;
                                        legend.chart.update();
                                    }
                                    return;
                                }
                                return;
                            }
                            const chart = legend.chart;

                            const forgettingLabel = this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.forgettingLabel');
                            const progressLabel = this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.learningProgressLabel');
                            const forgettingIndex = chart.data.datasets.findIndex(ds => ds.label === forgettingLabel);
                            const progressIndex = chart.data.datasets.findIndex(ds => ds.label === progressLabel);

                            if (index === progressIndex || index === forgettingIndex) {
                                const forgettingMeta = chart.getDatasetMeta(forgettingIndex);
                                const progressMeta = chart.getDatasetMeta(progressIndex);
                                const isCurrentlyHidden = progressMeta.hidden === true;

                                forgettingMeta.hidden = !isCurrentlyHidden;
                                progressMeta.hidden = !isCurrentlyHidden;
                            } else {
                                const meta = chart.getDatasetMeta(index);
                                meta.hidden = meta.hidden !== true;
                            }

                            chart.update();

                        },
                        labels: {
                            usePointStyle: true,
                            padding: 28,
                            generateLabels: (chart) => {
                                const base = Chart.defaults.plugins.legend.labels.generateLabels(chart);
                                const filteredBase = base.filter(label => {
                                    const learningGoalLabel = this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.learningGoalLabel');
                                    return !(label.text === learningGoalLabel && !hasGoal);
                                });

                                const annotationOptions = chart.options.plugins?.annotation;
                                const nowHidden = annotationOptions?.annotations?.now?.display === false;
                                const customLegendItems: LegendItem[] = [];
                                customLegendItems.push({
                                    text: this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.nowLabel'),
                                    fillStyle: colors.markerNow,
                                    strokeStyle: colors.markerNow,
                                    lineCap: 'butt',
                                    lineDash: [],
                                    lineWidth: 2,
                                    hidden: nowHidden
                                });
                                return filteredBase.concat(customLegendItems);
                            }
                        },
                        onHover: (event, legendItem, legend) => {
                            const canvas = legend.chart.canvas;
                            if (canvas !== undefined) {
                                canvas.style.cursor = 'pointer';
                            }
                        },
                        onLeave: (event, legendItem, legend) => {
                            const canvas = legend.chart.canvas;
                            if (canvas !== undefined) {
                                canvas.style.cursor = 'default';
                            }
                        }
                    },
                    tooltip: {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        position: 'leftOffset' as any,
                        xAlign: 'center',
                        boxPadding: 8,
                        animation: false,
                        bodySpacing: 6,
                        titleFont: {size: 12, weight: 'bold'},
                        footerFont: {size: 12, weight: 'normal'},
                        bodyFont: {size: 12, weight: 'normal'},
                        footerMarginTop: 8,
                        filter: (tooltipItem): boolean => {
                            if (fullHistory[tooltipItem.dataIndex].type === LearningHistoryTypes.MAX) {
                                return false;
                            }
                            return tooltipItem.dataIndex !== nowIdx && tooltipItem.dataIndex !== pointBeforeForgettingIdx;
                        },
                        callbacks: {
                            title: (items): string[] => this.getTooltipTitle(items, pointOfForgettingIdx, fullHistory),
                            footer: (context): string => this.getPointOfForgettingTooltip(pointOfForgettingIdx, context, pointOfForgettingDate),
                            label: (context): string => this.getTooltipLabel(context, pointOfForgettingIdx, forgetting),
                            labelColor: (context): TooltipLabelStyle | undefined => {
                                const datasetLabel = context.dataset.label;
                                const learningProgressLabel = this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.learningProgressLabel');
                                const index = context.dataIndex;
                                return index === pointOfForgettingIdx && datasetLabel === learningProgressLabel ? {
                                    borderColor: colors.progress,
                                    backgroundColor: colors.progress
                                } : undefined;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        type: 'time',
                        alignToPixels: true,
                        adapters: {date: {locale: this.dateFnsLocale(), options: {weekStartsOn: 1}}},
                        time: this.getScaleTimeSettings(),
                        offset: false,
                        ticks: {
                            source: 'labels',
                            autoSkip: false,
                            maxRotation: 90,
                            minRotation: 90,
                            labelOffset: -18,
                            callback: (value: string | number, index: number) => {
                                return index === 0 ? '' : this.formatTickByMode(Number(value));
                            }
                        },
                        min: periodEndTimestamps.length > 0 ? periodEndTimestamps[0] : undefined,
                        max: periodEndTimestamps.length > 0 ? periodEndTimestamps[periodEndTimestamps.length - 1] : undefined,
                        bounds: 'ticks',
                        grid: {color: colors.grid, offset: false}
                    },
                    xBars: {
                        type: 'time',
                        offset: false,
                        grid: {display: false},
                        adapters: {date: {locale: this.dateFnsLocale()}},
                        time: this.getScaleTimeSettings(),
                        display: false
                    },
                    xNow: {
                        type: 'time',
                        grid: {display: false},
                        time: this.getScaleTimeSettings(),
                        position: 'top',
                        offset: false,
                        bounds: 'ticks',
                        min: periodEndTimestamps.length > 0 ? periodEndTimestamps[0] : undefined,
                        max: periodEndTimestamps.length > 0 ? periodEndTimestamps[periodEndTimestamps.length - 1] : undefined,
                        ticks: {
                            source: 'labels',
                            autoSkip: false,
                            callback: (value: string | number, index: number) => this.displayNowLabel(index, nowIdx1)
                        },
                        afterBuildTicks: (axis) => this.addNowAnnotationLabel(axis, nowDate)
                    },
                    y: {
                        position: 'left',
                        min: 0,
                        max: 100,
                        title: {
                            display: true,
                            text: this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.learningProgressTooltip')
                        },
                        ticks: {
                            callback: (value: number | string) => {
                                const val = typeof value === 'string' ? parseFloat(value) : value;
                                const isGoalValue = hasGoal && Math.abs(val - goalValue) < 0.1;

                                if (!isGoalValue) {
                                    return val.toString();
                                }

                                const chart = this.chart;
                                if (chart !== undefined) {
                                    const goalLabel = this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.learningGoalLabel');
                                    const goalIndex = chart.data.datasets.findIndex(ds => ds.label === goalLabel);
                                    const isGoalHidden = goalIndex !== -1 ? chart.getDatasetMeta(goalIndex).hidden === true : false;
                                    const isDefaultTick = val % 20 === 0;

                                    if (isGoalHidden) {
                                        return !isDefaultTick ? '' : val;
                                    }
                                }

                                return `⚑ ${val}`;
                            },
                            color: (context) => {
                                const chart = context.chart;
                                const goalLabel = this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.learningGoalLabel');
                                const goalIndex = chart.data.datasets.findIndex(ds => ds.label === goalLabel);
                                const isGoalHidden = goalIndex !== -1 ? chart.getDatasetMeta(goalIndex).hidden === true : false;
                                const isGoalValue = hasGoal && Math.abs(context.tick.value - goalValue) < 0.1;
                                if (isGoalValue && !isGoalHidden) {
                                    return colors.goal;
                                }
                                return colors.tick;
                            }
                        },
                        afterBuildTicks: (axis) => {
                            if (hasGoal) {
                                const ticks = axis.ticks;
                                const goalTickExists = ticks.some(tick => Math.abs(tick.value - goalValue) < 0.1);
                                if (!goalTickExists) {
                                    ticks.push({value: goalValue, label: goalValue.toString()});
                                    ticks.sort((a, b) => a.value - b.value);
                                }
                            }
                        },
                        grid: {
                            color: (context) => {
                                const chart = context.chart;
                                const goalLabel = this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.learningGoalLabel');
                                const goalIndex = chart.data.datasets.findIndex(ds => ds.label === goalLabel);
                                const isGoalHidden = goalIndex !== -1 ? chart.getDatasetMeta(goalIndex).hidden === true : false;
                                const isGoalValue = hasGoal && Math.abs(context.tick.value - goalValue) < 0.1;
                                const isDefaultTick = context.tick.value % 20 === 0;
                                if (isGoalValue && isGoalHidden && !isDefaultTick) {
                                    return 'transparent';
                                }
                                return colors.grid;
                            }
                        }
                    },
                    yAnswers: {
                        position: 'right',
                        min: 0,
                        max: yAnswersMax,
                        title: {
                            display: true,
                            align: 'start',
                            text: this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.answeredQuestions')
                        },
                        grid: {drawOnChartArea: false},
                        ticks: {
                            callback: (v: number | string): string => {
                                const val = typeof v === 'string' ? parseFloat(v) : v;
                                if (val > yAnswersMax) {
                                    return '';
                                }
                                return `${Math.round(val)}`;
                            }
                        },
                        afterBuildTicks: (axis) => {
                            axis.ticks = HistoryDiagramCalculator.getTicsForAnswerSteps(steps);
                        }
                    }
                }
            },
            plugins: [
                {
                    id: 'whiteBackground',
                    beforeDraw: (chart): void => {
                        const ctx = chart.ctx;
                        ctx.save();
                        ctx.globalCompositeOperation = 'destination-over';
                        ctx.fillStyle = 'white';
                        ctx.fillRect(0, 0, chart.width, chart.height);
                        ctx.restore();
                    }
                }
            ]
        };

        const annotation: AnnotationPluginOptions = {annotations: {
                now: periodEndDates.length > 0
                    ? {
                        type: 'line',
                        xMin: nowDate.getTime(),
                        xMax: nowDate.getTime(),
                        borderColor: colors.markerNow,
                        borderWidth: LearningHistoryDiagramComponent.BORDER_WIDTH,
                        xScaleID: 'x',
                        drawTime: 'beforeDatasetsDraw'
                    }
                    : undefined,
                goalLabel: hasGoal && periodEndDates.length > 0
                    ? {
                        type: 'label',
                        display: true,
                        xValue: periodEndDates[0],
                        yValue: goalValue,
                        content: `${goalValue}`,
                        backgroundColor: 'transparent',
                        color: colors.cGoal,
                        padding: 0,
                        font: {weight: '600', size: 12},
                        position: 'start',
                        xAdjust: -40,
                        yAdjust: 0
                    }
                    : undefined
            }};
        config.options!.plugins!.annotation = annotation;

        this.destroyChart();
        const ctx = this.canvasRef.nativeElement.getContext('2d')!;
        this.chart = new Chart(ctx, config);
    }

    private displayNowLabel(index: number, nowIdx1: number): string {
        const chart = this.chart;
        if (chart !== undefined) {
            const ann = chart.options.plugins?.['annotation']?.annotations?.['now'];
            const isAnnotationVisible = ann?.display !== false;
            if (!isAnnotationVisible && index === nowIdx1) {
                return '';
            }
        }
        return index !== nowIdx1 ? '' : this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.nowLabel');
    }

    private addNowAnnotationLabel(axis: Scale<CoreScaleOptions>, nowDate: Date): void {
        const ticks = axis.ticks;
        ticks.push({
            value: nowDate.getTime(),
            label: this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.nowLabel')
        });
        ticks.sort((a, b) => a.value - b.value);
    }

    private getScaleTimeSettings(): TimeScaleTimeOptions {
        return {
            unit: this.timePeriodMode === TimePeriodMode.Daily
                ? 'day'
                : this.timePeriodMode === TimePeriodMode.Weekly
                    ? 'week'
                    : 'month',
            tooltipFormat: this.timePeriodMode === TimePeriodMode.Daily
                ? 'dd.MM'
                : this.timePeriodMode === TimePeriodMode.Weekly
                    ? 'LLL dd, yyyy'
                    : 'LLL yy'
        } as TimeScaleTimeOptions;
    }

    private getTooltipLabel(context: TooltipItem<'bar'>, pointOfForgettingIdx: number | undefined, forgetting: number[]): string {
        const learningGoalLabel = this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.learningGoalLabel');
        if (context.dataset.label === learningGoalLabel) {
            return '';
        }
        const datasetLabel = context.dataset.label;
        const learningProgressLabel = this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.learningProgressLabel');
        const forgettingLabel = this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.forgettingLabel');

        const value = context.parsed.y;
        const index = context.dataIndex;

        if (datasetLabel === forgettingLabel) {
            const forgettingValue = forgetting[index] ?? 0;
            return `${datasetLabel}: ${forgettingValue} %`;
        }

        if (datasetLabel === learningProgressLabel) {
            return `${datasetLabel}: ${value} %`;
        }

        return index !== pointOfForgettingIdx ? `${datasetLabel}: ${value}` : '';
    }

    private getPointOfForgettingTooltip(pointOfForgettingIdx: number | undefined, context: TooltipItem<'bar'>[], pointOfForgettingDate: Date | undefined): string {
        if (pointOfForgettingIdx !== undefined && context[0]?.dataIndex !== undefined && (context[0].dataIndex) === pointOfForgettingIdx) {
            const locale = this.translate.currentLang;
            const formattedDate = new Intl.DateTimeFormat(locale, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            }).format(pointOfForgettingDate);

            return `${this.translate.instant('learningGoal.insightsPopup.learningProgressHistory.timeOfForgettingLabel')}: ${formattedDate}`;

        }
        return '';
    }

    private getTooltipTitle(items: TooltipItem<'bar'>[], pointOfForgettingIdx: number | undefined, fullHistory: LearningHistoryResponseDto[]): string[] {
        if (items === undefined || items.length === 0) {
            return [];
        }
        const it = items[0];
        const index = it.dataIndex;
        if (index === pointOfForgettingIdx) {
            return [];
        }

        const date = fullHistory[index].periodEndDate;
        if (date === undefined) {
            return [];
        }
        return this.getLegendTitlePerMode(new Date(date).getTime());
    }

    private getLearningProgressLineColor(context: ScriptableContext<'line'>, colors: {
        [p: string]: string
    }, isProjection: boolean[]): string {
        const index = context.dataIndex;
        return isProjection[index] ? colors.projection : colors.progress;
    }
}
